import {
    Monitor, AlertCircle, Activity, Cpu, ShieldAlert, CheckCircle2, XCircle,
    Cctv,
    Server,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    X,
    AlertTriangle,
    Lightbulb,
    Network,
    Shield,
    Layers,
    SquareArrowOutUpRight,
    Bot,
    Router,
    Wifi,
    Printer,
    Smartphone
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useMemo, useRef } from "react";
import { showError } from "../../utils/toast";
import baseapi from "../../services/baseapi";
import {
    PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label, ComposedChart, Line, Area,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, BarChart, Bar,
} from "recharts";


import {
    useClientLevel
} from "../../hooks/useClientLevel";
import { useNavigate } from "react-router-dom";
import SingleDateRangeSelector from "../../components/SingleDateRangeSelector";



interface UnifiedStats {
    totalAssets: number;
    onlineAssets: number;
    offlineAssets: number;
    notAuthenticated?: number;
    cameras: { total: number; online: number; offline: number; not_authenticated?: number };
    servers: { total: number; online: number; offline: number; not_authenticated?: number };
    switches: { total: number; online: number; offline: number; not_authenticated?: number };
    unmanaged_switches: { total: number; online: number; offline: number; not_authenticated?: number };
    firewalls: { total: number; online: number; offline: number; not_authenticated?: number };
    averageUptime30d?: number;
    [key: string]: any;
}

interface IssueTypeAsset {
    asset_id: number;
    asset_name: string;
    ip_address: string;
    updated: string;
    issue: {
        issue: string;
        category: string;
        description: string;
        recommendation: string;
    };
}

interface IssueTypeEntry {
    severity: 'critical' | 'warning';
    totalAssets: number;
    assets: IssueTypeAsset[];
}

interface IssueStats {
    totalIssueTypes: number;
    totalCriticalIssues: number;
    totalWarningIssues: number;
    totalAffectedAssets: number;
}

interface DashboardEvent {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    event_type: string;
    organization_id: number;
    client_id: number;
    asset_id: number;
    agent_id: number;
    message: string;
    description: string;
    event_data: string;
    asset_hostname: string;
    asset_ip: string;
    agent_name: string;
    severity: 'critical' | 'warning' | 'info';
    event_status?: string;
    ticket_id?: number;
    ticket_number?: string;
}

interface ResourceUtilization {
    topMemoryUtilization: {
        asset_id: number;
        asset_name: string;
        ip_address: string;
        memory_percentage: number | null;
        ram_percentage: number | null;
        os_name: string | null;
    }[];
    topStorageUtilization: {
        asset_id: number;
        asset_name: string;
        ip_address: string;
        storage_percentage: number | null;
        os_name: string | null;
    }[];
}

const StatusPill = ({ label, color, onClick }: any) => {
    let colorClasses = 'bg-rose-500/10 text-rose-400 border-rose-500/20 hover:bg-rose-500/20';
    let dotClass = 'bg-rose-500';

    if (color === 'emerald') {
        colorClasses = 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20';
        dotClass = 'bg-emerald-500 animate-pulse';
    } else if (color === 'orange') {
        colorClasses = 'bg-orange-500/10 text-orange-400 border-orange-500/20 hover:bg-orange-500/20';
        dotClass = 'bg-orange-500';
    }

    return (
        <div
            onClick={(e) => { e.stopPropagation(); onClick?.(); }}
            className={`px-1.5 py-0.5 ${colorClasses} rounded flex items-center gap-1 border cursor-pointer transition-all active:scale-95`}
        >
            <div className={`w-1 h-1 rounded-full ${dotClass} `} />
            <span className="text-[9px] font-black uppercase tracking-tight leading-none">{label}</span>
        </div>
    );
};

const SummaryCard = ({ icon: Icon, label, value, iconColor, bgColor, onClick, status, subLabel }: any) => (
    <div
        onClick={onClick}
        className={`group flex items-center justify-between bg-gray-800/40 border border-gray-700/60 rounded-xl p-2 shadow-xl backdrop-blur-sm transition-all duration-300 hover:border-gray-600 hover:bg-gray-800/60 ${onClick ? 'cursor-pointer active:scale-[0.99]' : ''} w-full`}
    >
        <div className="flex items-center gap-2.5 overflow-hidden">
            <div className={`p-2 rounded-lg ${bgColor} shrink-0 transition-transform group-hover:scale-110 duration-300`}>
                <Icon className={`w-4 h-4 ${iconColor} `} />
            </div>
            <div className="flex flex-col min-w-0">
                <span className="text-[10px] uppercase font-bold text-gray-100 tracking-widest leading-none mb-1.5">{label}</span>
                <div className="flex flex-rows">
                    <span className="text-lg font-black text-white leading-none tracking-tight">{value}</span>
                    {subLabel && <span className="text-[9px] text-gray-100 font-bold uppercase mt-1 tracking-tighter truncate">&nbsp;&nbsp; / {subLabel}</span>}
                </div>
            </div>
        </div>
        {status && (
            <div className="flex flex-col gap-1 pl-2.5 border-l border-gray-400 shrink-0">
                {status}
            </div>
        )}
    </div>
);

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-gray-700 border border-gray-600 p-2 rounded-lg shadow-xl z-50">
                <p className="text-gray-100 text-[10px] font-bold mb-1 uppercase tracking-wider">{payload[0].payload.displayName || label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-xs mb-0.5">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color || entry.payload.fill || entry.fill }} />
                        <span className="text-gray-100">{entry.name}:</span>
                        <span className="text-white font-mono font-medium">{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const RotatedTick = (props: any) => {
    const { x, y, payload } = props;
    const value = payload.value || "";
    const truncatedValue = value.length > 12 ? value.substring(0, 12) + "..." : value;

    return (
        <g transform={`translate(${x}, ${y})`}>
            <text
                x={0}
                y={0}
                dy={10}
                textAnchor="end"
                fill="#f9fafbff"
                fontSize={9}
                transform="rotate(-60)"
            >
                <title>{value}</title>
                {truncatedValue}
            </text>
        </g>
    );
};

// --- Issue type row for the main table ---
interface IssueTypeRow {
    issueName: string;
    severity: 'critical' | 'warning';
    totalAssets: number;
    assets: IssueTypeAsset[];
}

const getIconForKey = (key: string) => {
    switch (key.toLowerCase()) {
        case "cameras":
        case "camera":
            return Cctv;
        case "servers":
        case "server":
        case "local_asset":
        case "endpoint_servers":
            return Server;
        case "switches":
        case "switch":
            return Network;
        case "unmanaged_switches":
        case "unmanaged_switch":
            return Layers;
        case "firewalls":
        case "firewall":
            return Shield;
        case "routers":
        case "router":
            return Router;
        case "access_points":
        case "access_point":
        case "ap":
            return Wifi;
        case "printers":
        case "printer":
            return Printer;
        case "mobiles":
        case "mobile":
            return Smartphone;
        default:
            return Monitor;
    }
};

const getColorClassesForKey = (key: string) => {
    switch (key.toLowerCase()) {
        case "cameras":
        case "camera":
            return { color: "text-sky-500", bg: "bg-sky-500/10" };
        case "servers":
        case "server":
        case "local_asset":
        case "endpoint_servers":
            return { color: "text-violet-500", bg: "bg-violet-500/10" };
        case "switches":
        case "switch":
            return { color: "text-orange-500", bg: "bg-orange-500/10" };
        case "unmanaged_switches":
        case "unmanaged_switch":
            return { color: "text-cyan-500", bg: "bg-cyan-500/10" };
        case "firewalls":
        case "firewall":
            return { color: "text-indigo-500", bg: "bg-indigo-500/10" };
        case "routers":
        case "router":
            return { color: "text-rose-500", bg: "bg-rose-500/10" };
        case "access_points":
        case "access_point":
            return { color: "text-emerald-500", bg: "bg-emerald-500/10" };
        case "printers":
        case "printer":
            return { color: "text-yellow-500", bg: "bg-yellow-500/10" };
        case "mobiles":
        case "mobile":
            return { color: "text-green-500", bg: "bg-green-500/10" };
        default:
            return { color: "text-blue-500", bg: "bg-blue-500/10" };
    }
};

const getAssetTypeForKey = (key: string) => {
    switch (key.toLowerCase()) {
        case "servers":
            return "server";
        case "endpoint_servers":
            return "local_asset";
        case "mobiles":
            return "mobile";
        case 'network_devices':
            return "network_device"
        default:
            if (key.endsWith("es")) {
                return key.slice(0, -2);
            }
            if (key.endsWith("s")) {
                return key.slice(0, -1);
            }
            return key;
    }
};

const Dashboard = () => {
    const navigate = useNavigate();
    const currentLevel = useClientLevel();



    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<UnifiedStats | null>(null);
    const [issues, setIssues] = useState<{ stats: IssueStats; issueTypes: Record<string, IssueTypeEntry>; } | null>(null);
    const [resources, setResources] = useState<ResourceUtilization | null>(null);
    const [assetsUptime, setAssetsUptime] = useState<any[]>([]);
    const [uptimeAssetType, setUptimeAssetType] = useState<'camera' | 'local_asset'>('camera');
    const [dashboardEvents, setDashboardEvents] = useState<DashboardEvent[]>([]);
    const [eventSummary, setEventSummary] = useState<{
        total_24h: number;
        by_severity: { critical: number; warning: number; info: number };
    } | null>(null);
    const [totalEvents, setTotalEvents] = useState(0);
    const [eventsLoading, setEventsLoading] = useState(false);



    // Table pagination
    const ROWS_PER_PAGE = 5;
    const [tablePage, setTablePage] = useState(0);
    const [issueFilterSeverity, setIssueFilterSeverity] = useState<string | null>(null);

    // Side panel
    const [selectedIssueType, setSelectedIssueType] = useState<IssueTypeRow | null>(null);
    const [issuePanelPage, setIssuePanelPage] = useState(0);
    const ISSUE_PANEL_ROWS = 10;

    // Resource panel
    const [resourcePanelType, setResourcePanelType] = useState<'memory' | 'storage' | null>(null);
    const [resourcePanelPage, setResourcePanelPage] = useState(0);
    const RESOURCE_ROWS_PER_PAGE = 10;

    // Events panel
    const [selectedEvent, setSelectedEvent] = useState<DashboardEvent | null>(null);
    const [eventTablePage, setEventTablePage] = useState(0);
    const [eventSeverity, setEventSeverity] = useState<string | null>(null);
    const last24HoursUTC = new Date(Date.now() - 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 19);
    const [eventDateFilter, setEventDateFilter] = useState<any>(last24HoursUTC)
    const [avgUptime, setAvgUptime] = useState<any>(null);

    const EVENTS_PER_PAGE = 5;

    // Uptime dropdown
    const [isUptimeDropdownOpen, setIsUptimeDropdownOpen] = useState(false);

    const COLORS = [
        "#0ea5e9", // blue
        "#8b5cf6", // violet
        "#f97316", // orange
        "#22c55e", // green
        "#eab308", // yellow
        "#ef4444", // red
        "#14b8a6", // teal
        "#6366f1", // indigo
        "#ec4899", // pink
        "#84cc16", // lime
    ];

    // Scroll refs (kept for stat cards)
    const issuesTableRef = useRef<HTMLDivElement>(null);

    const scrollToCritical = () => {
        issuesTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const scrollToWarning = () => {
        issuesTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const getVisiblePages = (current: number, total: number) => {
        if (total <= 3) return Array.from({ length: total }, (_, i) => i);
        let start = Math.max(0, current - 1);
        if (start + 3 > total) start = total - 3;
        return [start, start + 1, start + 2];
    };




    useEffect(() => {
    }, []);

    useEffect(() => {
        if (currentLevel) {
            setEventTablePage(0);
            setTablePage(0);
            setResourcePanelPage(0);
            setEventSeverity(null);
            setIssueFilterSeverity(null);
            fetchAllDashboardData();
        }
    }, [currentLevel]);

    useEffect(() => {
        if (currentLevel) {
            fetchDashboardEvents(eventTablePage);
        }
    }, [currentLevel, eventTablePage, eventSeverity, eventDateFilter]);

    useEffect(() => {
        if (currentLevel) {
            fetchAssetsUptime();
        }
    }, [currentLevel, uptimeAssetType]);



    const fetchAssetsUptime = async () => {
        try {
            const client_id = currentLevel.name === "global" ? null : currentLevel.id;
            const params = {
                ...(client_id ? { client_id } : {}),
                asset_type: uptimeAssetType
            };
            const response = await baseapi.get("/dashboard/assets_uptime", { params });

            if (response.data.status) {
                const data = response.data.data;
                setAssetsUptime(data.assets || data.trend || data.cameras || (Array.isArray(data) ? data : []));
                if (data.stats?.averageUptime30d !== undefined) {
                    setAvgUptime(data.stats.averageUptime30d);
                }
            }
        } catch (error: any) {
            console.error("Failed to fetch assets uptime", error);
        }
    };

    const fetchDashboardEvents = async (page: number) => {
        setEventsLoading(true);

        try {
            const client_id = currentLevel.name === "global" ? null : currentLevel.id;

            const query_severty = {
                ...(eventSeverity ? { severity: eventSeverity } : {})
            };

            const query_obj = {
                ...(client_id ? { client_id: client_id } : {})
            };

            const params = {
                skip: page * EVENTS_PER_PAGE,
                limit: EVENTS_PER_PAGE,
                created_after: eventDateFilter,
                query: JSON.stringify(query_obj),
                ...query_severty
            };



            const response = await baseapi.get("/events", { params });

            if (response.data.status) {
                setDashboardEvents(response.data.data || []);
                setTotalEvents(response.data.total || 0);

                if (response.data.summary) {
                    setEventSummary(response.data.summary);
                }
            }

        } catch (error: any) {
            console.error("Failed to fetch events", error);
        } finally {
            setEventsLoading(false);
        }
    };

    const fetchAllDashboardData = async () => {
        setLoading(true);
        try {
            const client_id = currentLevel.name === "global" ? null : currentLevel.id;
            const params = client_id ? { client_id } : {};

            const [assetRes, issueRes, resourceRes] = await Promise.all([
                baseapi.get("/dashboard/unified_asset_overview", { params }),
                baseapi.get("/dashboard/asset_issues", { params }),
                baseapi.get("/dashboard/resource_utilization", { params }),
            ]);

            if (assetRes.data.status) setStats(assetRes.data.data.stats);
            if (issueRes.data.status) setIssues(issueRes.data.data);
            if (resourceRes.data.status) setResources(resourceRes.data.data);


        } catch (error: any) {
            showError("Failed to fetch dashboard data");
        } finally {
            setLoading(false);
        }
    };

    const assetStatusData = useMemo(() => {
        if (!stats) return [];
        return [
            { name: "Online", value: stats.onlineAssets, color: "#10b981" }, // emerald-500
            { name: "Offline", value: stats.offlineAssets, color: "#f43f5e" }, // rose-500
        ];
    }, [stats]);

    const assetTypeData = useMemo(() => {
        if (!stats) return [];

        return Object.entries(stats)
            .filter(([_, value]: any) =>
                typeof value === "object" && value !== null && "total" in value
            )
            .map(([key, value]: any, index) => ({
                name: key.toLowerCase() === "servers"
                    ? "EndPoint Servers"
                    : key
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c: string) => c.toUpperCase()),
                value: value.total,
                color: COLORS[index % COLORS.length], // 🔥 cycles through 10 colors
            }));

    }, [stats]);

    // Full data (keep all for the slide panel table)
    const allMemoryData = useMemo(() => {
        if (!resources) return [];
        return resources.topMemoryUtilization;
    }, [resources]);

    const allStorageData = useMemo(() => {
        if (!resources) return [];
        return resources.topStorageUtilization;
    }, [resources]);

    // Top 5 non-null for charts
    const memoryData = useMemo(() => {
        return allMemoryData
            .filter(item => item.ram_percentage !== null)
            .slice(0, 5)
            .map(item => ({
                displayName: item.asset_name,
                name: `${item.asset_name} (#${item.asset_id})`,
                value: item.ram_percentage as number
            }));
    }, [allMemoryData]);

    const storageData = useMemo(() => {
        return allStorageData
            .filter(item => item.storage_percentage !== null)
            .slice(0, 5)
            .map(item => ({
                displayName: item.asset_name,
                name: `${item.asset_name} (#${item.asset_id})`,
                value: item.storage_percentage as number
            }));
    }, [allStorageData]);

    const uptimeChartData = useMemo(() => {
        if (!assetsUptime || assetsUptime.length === 0) return [];
        return assetsUptime.map(item => ({
            displayName: item.asset_name || item.label || item.date || "Unknown",
            name: `${item.asset_name || item.label || item.date || "Unknown"} (#${item.asset_id || Math.random()})`,
            value: item.uptime ?? item.value ?? item.uptime_30d_percentage ?? 0
        }));
    }, [assetsUptime]);





    if (currentLevel.install_first_agent) {
        return (<div className="h-full bg-gray-800 text-gray-100 font-sans overflow-hidden flex flex-col">
            <div className="flex justify-center items-center h-full">
                <div className="">
                    <div className="flex flex-col items-center">
                        <div className="bg-orange-600 rounded-full animate-pulse p-2 mb-4">
                            <Bot size={40} />
                        </div>
                        <div className="text-xl font-bold mb-1 flex gap-2 items-center mb-2">

                            Gain Control Over Your Network</div>
                        <div className="text-gray-400 text-xs">Install the agent to identify and continuously monitor</div>
                        <div className="text-gray-400 text-xs mb-2">all internal devices, including switches and user systems</div>
                        <button className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg" onClick={() => navigate('/agents')}>Go to Agents</button>
                    </div>
                </div>
            </div>


        </div>)
    }

    return (
        <div className="h-full bg-gray-900 text-gray-100 font-sans overflow-hidden flex flex-col">

            {/* Dashboard Stats Section */}
            {!loading && (
                <div className="flex-none px-2 pt-1 pb-1 z-20">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-6 gap-2 w-full">

                        {/* 1. Total Assets */}
                        <SummaryCard
                            icon={Monitor} label="Total Assets"
                            value={stats?.totalAssets || 0}
                            iconColor="text-blue-500" bgColor="bg-blue-500/10"
                            onClick={() => navigate('/asset')}
                            status={
                                <>
                                    <StatusPill label={`${stats?.onlineAssets || 0} Online`} color="emerald" onClick={() => navigate('/asset?status=active')} />
                                    <StatusPill label={`${stats?.offlineAssets || 0} Offline`} color="rose" onClick={() => navigate('/asset?status=offline')} />
                                    {stats?.notAuthenticated !== undefined && (
                                        <StatusPill label={`${stats?.notAuthenticated || 0} No Auth`} color="orange" onClick={() => navigate('/asset?is_auth=false')} />
                                    )}
                                </>
                            }
                        />

                        {/* Dynamic Category Cards */}
                        {stats && Object.entries(stats).map(([key, val]) => {
                            // Skip non-object fields or fields that don't have total structure
                            if (!val || typeof val !== "object" || !("total" in val)) return null;

                            const rawLabel = key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
                            const label = rawLabel;
                            const IconComponent = getIconForKey(key);
                            const colors = getColorClassesForKey(key);
                            const assetType = getAssetTypeForKey(key);

                            const totalVal = (val as any).total ?? 0;
                            const onlineVal = (val as any).online ?? 0;
                            const offlineVal = (val as any).offline ?? 0;
                            const notAuthVal = (val as any).not_authenticated;

                            return (
                                <SummaryCard
                                    key={key}
                                    icon={IconComponent}
                                    label={label}
                                    value={totalVal}
                                    iconColor={colors.color}
                                    bgColor={colors.bg}
                                    onClick={() => navigate(`/asset?asset_type=${assetType}`)}
                                    status={
                                        <>
                                            <StatusPill label={`${onlineVal} Online`} color="emerald" onClick={() => navigate(`/asset?asset_type=${assetType}&status=active`)} />
                                            <StatusPill label={`${offlineVal} Offline`} color="rose" onClick={() => navigate(`/asset?asset_type=${assetType}&status=offline`)} />
                                            {notAuthVal !== undefined && (
                                                <StatusPill label={`${notAuthVal} No Auth`} color="orange" onClick={() => navigate(`/asset?asset_type=${assetType}&is_auth=true`)} />
                                            )}
                                        </>
                                    }
                                />
                            );
                        })}

                        {/* 7. Critical Alerts */}
                        <SummaryCard
                            icon={ShieldAlert} label="Critical Alerts"
                            value={issues?.stats.totalCriticalIssues || 0}
                            iconColor="text-rose-500" bgColor="bg-rose-500/10"
                            onClick={scrollToCritical}
                            subLabel={`Across ${issues?.stats.totalAffectedAssets || 0} assets`}
                            status={
                                <div className="flex flex-col gap-1 items-end">
                                    <span className="text-[10px] font-bold text-rose-500 tracking-wider">ACTION</span>
                                    <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest">Required</span>
                                </div>
                            }
                        />

                        {/* 8. Warnings */}
                        <SummaryCard
                            icon={AlertCircle} label="Warnings"
                            value={issues?.stats.totalWarningIssues || 0}
                            iconColor="text-amber-500" bgColor="bg-amber-500/10"
                            onClick={scrollToWarning}
                            subLabel={`Across ${issues?.stats.totalAffectedAssets || 0} assets`}
                            status={
                                <div className="flex flex-col gap-1 items-end">
                                    <span className="text-[10px] font-bold text-amber-500 tracking-wider">REVIEW</span>
                                    <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest">Advised</span>
                                </div>
                            }
                        />

                    </div>
                </div>
            )}

            {/* MAIN CONTENT GRID - ROW BASED LAYOUT */}
            <div className="flex-1 flex flex-col px-2 py-1 gap-2 overflow-hidden">
                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-100">
                        <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mb-3" />
                        <p className="text-[11px] font-bold uppercase tracking-widest animate-pulse">Gathering Intelligence...</p>
                    </div>
                ) : (
                    <>
                        {/* TOP ROW: DISTRIBUTION & RESOURCE LOAD */}
                        <div className="flex-[1] min-h-0 flex gap-2">
                            {/* Distribution */}
                            <div className="w-1/3 bg-gray-800 rounded-xl border border-gray-700 p-2 shadow-sm flex flex-col">
                                <div className="flex items-center justify-between mb-2 flex-none">
                                    <h3 className="text-[11px] font-bold text-gray-100 uppercase tracking-wide flex items-center gap-2">
                                        <div className="p-1 rounded bg-indigo-500/10 text-indigo-400"><Activity className="w-3.5 h-3.5" /></div>
                                        Asset Distribution
                                    </h3>
                                </div>
                                <div className="flex-1 min-h-0 flex">
                                    <div className="w-1/2 flex flex-col items-center h-full">
                                        <div className="flex-1 w-full min-h-0">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={assetStatusData}
                                                        innerRadius="55%"
                                                        outerRadius="80%"
                                                        paddingAngle={4}
                                                        dataKey="value"
                                                        cornerRadius={5}
                                                    >
                                                        {assetStatusData.map((e, i) => (
                                                            <Cell key={i} fill={e.color} stroke="none" />
                                                        ))}
                                                        <Label
                                                            content={({ viewBox: { cx, cy } }: any) => {
                                                                // cx and cy represent the exact center of the pie chart
                                                                return (
                                                                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                                                                        {/* The Total Number */}
                                                                        <tspan x={cx} dy="-0.1em" fill="#f8fafc" fontSize="18" fontWeight="bold">
                                                                            {stats?.totalAssets || 0}
                                                                        </tspan>

                                                                        {/* 2. The Marking/Label below the number */}
                                                                        <tspan x={cx} dy="1.4em" fill="#f9fafbff" fontSize="10" fontWeight="600" className="uppercase tracking-widest">
                                                                            Total
                                                                        </tspan>
                                                                    </text>
                                                                );
                                                            }}
                                                        />
                                                    </Pie>
                                                    <Tooltip content={<CustomTooltip />} />
                                                </PieChart>
                                            </ResponsiveContainer>

                                        </div>
                                        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-1 px-2">
                                            {assetStatusData.map((e, i) => (
                                                <div key={i} className="flex items-center gap-1.5 whitespace-nowrap">
                                                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color }} />
                                                    <span className="text-[9px] text-gray-100 font-bold uppercase tracking-tight">{e.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="w-1/2 flex flex-col items-center h-full border-l border-gray-700">
                                        <div className="flex-1 w-full min-h-0">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={assetTypeData}
                                                        innerRadius="55%"
                                                        outerRadius="80%"
                                                        dataKey="value"

                                                    >
                                                        {assetTypeData.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}

                                                        {/* Added Label for Center Text */}
                                                        <Label
                                                            content={({ viewBox: { cx, cy } }: any) => {
                                                                return (
                                                                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                                                                        <tspan x={cx} dy="-0.1em" fill="#f8fafc" fontSize="18" fontWeight="bold">
                                                                            {stats?.totalAssets || 0}
                                                                        </tspan>
                                                                        <tspan x={cx} dy="1.4em" fill="#f9fafbff" fontSize="10" fontWeight="600" className="uppercase tracking-widest">
                                                                            Assets
                                                                        </tspan>
                                                                    </text>
                                                                );
                                                            }}
                                                        />
                                                    </Pie>
                                                    <Tooltip content={<CustomTooltip />} />
                                                </PieChart>
                                            </ResponsiveContainer>

                                        </div>
                                        <div className="flex flex-wrap max-h-[35px] custom-scroll overflow-y-auto justify-center gap-x-3 gap-y-1 mt-1 px-2 w-full">
                                            {assetTypeData.map((e, i) => (
                                                <div key={i} className="flex items-center gap-1.5 whitespace-nowrap">
                                                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color }} />
                                                    <span className="text-[9px] text-gray-100 font-bold uppercase tracking-tight">{e.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── Resource Load — Radar + Ranked Bars ── */}
                            <div className="w-2/3 bg-gray-800 rounded-xl border border-gray-700 p-2 shadow-sm flex flex-col">
                                <div className="flex items-center justify-between mb-2 flex-none border-b border-gray-700 pb-2">
                                    <h3 className="text-[11px] font-bold text-gray-100 uppercase tracking-wide flex items-center gap-2">
                                        <div className="p-1 rounded bg-blue-500/10 text-blue-400"><Cpu className="w-3.5 h-3.5" /></div>
                                        Resource Load
                                    </h3>
                                    <div className="flex items-center gap-3 text-[8px]">
                                        <span className="flex items-center gap-1 text-gray-100"><span className="w-1.5 h-1.5 rounded-full bg-violet-500 inline-block" />Memory</span>
                                        <span className="flex items-center gap-1 text-gray-100"><span className="w-1.5 h-1.5 rounded-full bg-sky-500 inline-block" />Storage</span>
                                    </div>
                                </div>

                                <div className="flex-1 min-h-0 flex gap-2 overflow-hidden">

                                    {/* ── MEMORY: Radar / Spider Chart (Top 5) ── */}
                                    <div className="w-1/2 flex flex-col h-full">
                                        <div className="flex items-center justify-between mb-1">
                                            <p className="text-[9px] text-gray-100 font-bold uppercase tracking-wider">Memory (Top 5)</p>
                                            <button
                                                onClick={() => { setResourcePanelType('memory'); setResourcePanelPage(0); }}
                                                className="text-[8px] text-blue-400 hover:text-blue-300 font-bold uppercase tracking-wide transition-colors"
                                            >
                                                Show All →
                                            </button>
                                        </div>
                                        <div className="flex-1 min-h-0">
                                            {memoryData.length === 0 ? (
                                                <div className="h-full flex items-center justify-center text-[10px] text-gray-100">No data</div>
                                            ) : (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <RadarChart data={memoryData} cx="50%" cy="50%" outerRadius="90%">
                                                        <PolarGrid stroke="#7a7c81ff" />
                                                        <PolarAngleAxis
                                                            dataKey="name"
                                                            tick={({ x, y, payload }: any) => {
                                                                const truncated = payload.value?.length > 20 ? payload.value.substring(0, 20) + '…' : payload.value;
                                                                return (
                                                                    <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fill="#f9fafbff" fontSize={8}>
                                                                        <title>{payload.value}</title>
                                                                        {truncated}
                                                                    </text>
                                                                );
                                                            }}
                                                        />
                                                        <PolarRadiusAxis
                                                            domain={[0, 100]}
                                                            tick={{ fill: '#82868aff', fontSize: 7 }}
                                                            axisLine={false}
                                                            tickCount={4}
                                                        />
                                                        <Radar
                                                            name="RAM %"
                                                            dataKey="value"
                                                            stroke="#8b5cf6"
                                                            strokeWidth={2}
                                                            fill="#8b5cf6"
                                                            fillOpacity={0.2}
                                                            dot={{ r: 3, fill: '#8b5cf6', stroke: '#0f172a', strokeWidth: 1.5 }}
                                                        />
                                                        <Tooltip
                                                            content={({ active, payload }: any) => {
                                                                if (!active || !payload?.length) return null;
                                                                const d = payload[0].payload;
                                                                const isCrit = d.value > 80;
                                                                const isWarn = d.value > 60;
                                                                const col = isCrit ? '#f43f5e' : isWarn ? '#f59e0b' : '#8b5cf6';
                                                                return (
                                                                    <div className="bg-gray-700/95 border border-gray-600 rounded-lg px-3 py-2 shadow-xl backdrop-blur-sm">
                                                                        <p className="text-[10px] text-gray-100 font-bold mb-0.5">{d.displayName || d.name}</p>
                                                                        <p className="text-[10px] font-mono font-bold" style={{ color: col }}>{d.value}%</p>
                                                                    </div>
                                                                );
                                                            }}
                                                        />
                                                    </RadarChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>

                                    {/* ── STORAGE: Horizontal Ranked Bar Chart (Top 5) ── */}
                                    <div className="w-1/2 flex flex-col h-full border-l border-gray-700 pl-2">
                                        <div className="flex items-center justify-between mb-1">
                                            <p className="text-[9px] text-gray-100 font-bold uppercase tracking-wider">Storage (Top 5)</p>
                                            <button
                                                onClick={() => { setResourcePanelType('storage'); setResourcePanelPage(0); }}
                                                className="text-[8px] text-blue-400 hover:text-blue-300 font-bold uppercase tracking-wide transition-colors"
                                            >
                                                Show All →
                                            </button>
                                        </div>
                                        <div className="flex-1 min-h-0">
                                            {storageData.length === 0 ? (
                                                <div className="h-full flex items-center justify-center text-[10px] text-gray-100">No data</div>
                                            ) : (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={[...storageData].sort((a, b) => b.value - a.value)}
                                                        layout="vertical"
                                                        margin={{ top: 5, right: 25, left: 5, bottom: 5 }}
                                                        barCategoryGap="20%"
                                                    >
                                                        <defs>
                                                            <linearGradient id="barGradNormal" x1="0" y1="0" x2="1" y2="0">
                                                                <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.3} />
                                                                <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.9} />
                                                            </linearGradient>
                                                            <linearGradient id="barGradWarn" x1="0" y1="0" x2="1" y2="0">
                                                                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                                                                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.9} />
                                                            </linearGradient>
                                                            <linearGradient id="barGradCrit" x1="0" y1="0" x2="1" y2="0">
                                                                <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.3} />
                                                                <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.9} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#636568ff" horizontal={false} />
                                                        <XAxis
                                                            type="number"
                                                            domain={[0, 100]}
                                                            axisLine={false}
                                                            tickLine={false}
                                                            tick={{ fill: '#82868aff', fontSize: 8 }}
                                                            tickCount={5}
                                                        />
                                                        <YAxis
                                                            type="category"
                                                            dataKey="name"
                                                            axisLine={false}
                                                            tickLine={false}
                                                            width={75}
                                                            interval={0}
                                                            tick={({ x, y, payload }: any) => {
                                                                const t = payload.value?.length > 12 ? payload.value.substring(0, 12) + '…' : payload.value;
                                                                return (
                                                                    <text x={x - 4} y={y} textAnchor="end" dominantBaseline="central" fill="#f9fafbff" fontSize={8}>
                                                                        <title>{payload.value}</title>
                                                                        {t}
                                                                    </text>
                                                                );
                                                            }}
                                                        />
                                                        <Tooltip
                                                            content={({ active, payload }: any) => {
                                                                if (!active || !payload?.length) return null;
                                                                const d = payload[0].payload;
                                                                const isCrit = d.value > 90;
                                                                const isWarn = d.value > 80;
                                                                const col = isCrit ? '#f43f5e' : isWarn ? '#f59e0b' : '#0ea5e9';
                                                                return (
                                                                    <div className="bg-gray-700/95 border border-gray-600 rounded-lg px-3 py-2 shadow-xl backdrop-blur-sm">
                                                                        <p className="text-[10px] text-gray-100 font-bold mb-0.5">{d.displayName || d.name}</p>
                                                                        <p className="text-[10px] font-mono font-bold" style={{ color: col }}>{d.value}%</p>
                                                                    </div>
                                                                );
                                                            }}
                                                            cursor={{ fill: '#334155', opacity: 0.2 }}
                                                        />
                                                        <Bar
                                                            dataKey="value"
                                                            radius={[0, 4, 4, 0]}
                                                            label={({ x, y, width: w, height: h, value }: any) => (
                                                                <text
                                                                    x={x + w + 4}
                                                                    y={y + h / 2}
                                                                    dominantBaseline="central"
                                                                    fill={value > 90 ? '#f43f5e' : value > 80 ? '#f59e0b' : '#0ea5e9'}
                                                                    fontSize={8}
                                                                    fontWeight="bold"
                                                                    fontFamily="monospace"
                                                                >
                                                                    {value}%
                                                                </text>
                                                            )}
                                                        >
                                                            {[...storageData].sort((a, b) => b.value - a.value).map((entry, i) => (
                                                                <Cell
                                                                    key={i}
                                                                    fill={entry.value > 90 ? 'url(#barGradCrit)' : entry.value > 80 ? 'url(#barGradWarn)' : 'url(#barGradNormal)'}
                                                                />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </div>

                        </div>

                        {/* BOTTOM ROW: CRITICAL & WARNING ISSUES + UPTIME */}
                        <div className="flex-[1.2] min-h-0 flex gap-2">
                            {/* 3. CAMERA UPTIME TREND PANEL (1/3 Width) */}
                            <div className="w-1/3 bg-gray-800 rounded-xl border border-gray-700 p-2 shadow-sm flex flex-col">
                                <div className="flex items-center justify-between mb-2 flex-none border-b border-gray-700 pb-2">
                                    <h3 className="text-[11px] font-bold text-gray-100 uppercase tracking-wide flex items-center gap-2">
                                        <div className="p-1 rounded bg-emerald-500/10 text-emerald-400"><Monitor className="w-3.5 h-3.5" /></div>
                                        Assets Uptime
                                    </h3>
                                    <div className="flex items-center gap-3">
                                        <div className="relative">
                                            <button
                                                onClick={() => setIsUptimeDropdownOpen(!isUptimeDropdownOpen)}
                                                className="flex items-center gap-2 px-2.5 py-1 bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-lg transition-all active:scale-95"
                                            >
                                                <span className="text-[9px] font-bold text-gray-100 uppercase tracking-widest leading-none">
                                                    {uptimeAssetType === 'camera' ? 'Camera' : 'Server'}
                                                </span>
                                                <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isUptimeDropdownOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            <AnimatePresence>
                                                {isUptimeDropdownOpen && (
                                                    <>
                                                        <div
                                                            className="fixed inset-0 z-30"
                                                            onClick={() => setIsUptimeDropdownOpen(false)}
                                                        />
                                                        <motion.div
                                                            initial={{ opacity: 0, y: 4, scale: 0.95 }}
                                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                                            exit={{ opacity: 0, y: 4, scale: 0.95 }}
                                                            transition={{ duration: 0.1 }}
                                                            className="absolute right-0 top-full mt-1 w-24 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl z-40 overflow-hidden py-1"
                                                        >
                                                            <button
                                                                onClick={() => {
                                                                    setUptimeAssetType('camera');
                                                                    setIsUptimeDropdownOpen(false);
                                                                }}
                                                                className={`w-full px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest text-left transition-colors ${uptimeAssetType === 'camera' ? 'bg-emerald-500/10 text-emerald-400' : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'}`}
                                                            >
                                                                Camera
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setUptimeAssetType('local_asset');
                                                                    setIsUptimeDropdownOpen(false);
                                                                }}
                                                                className={`w-full px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest text-left transition-colors ${uptimeAssetType === 'local_asset' ? 'bg-emerald-500/10 text-emerald-400' : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'}`}
                                                            >
                                                                Server
                                                            </button>
                                                        </motion.div>
                                                    </>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                        {avgUptime !== undefined && (
                                            <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                                {avgUptime}% Avg
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex-1 min-h-0 overflow-x-auto custom-scroll pb-2">
                                    <div style={{ width: `${Math.max(100, uptimeChartData.length * 8)}% `, height: '100%' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={uptimeChartData} margin={{ top: 10, right: 10, left: -25, bottom: 45 }}>
                                                <defs>
                                                    <linearGradient id="colorUptime" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#4e5052ff" vertical={false} />
                                                <XAxis
                                                    dataKey="name"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={<RotatedTick />}
                                                    interval={0}
                                                />
                                                <YAxis
                                                    type="number"
                                                    domain={[0, 100]}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fill: '#f9fafbff', fontSize: 8 }}
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Area type="monotone" dataKey="value" stroke="none" fill="url(#colorUptime)" />
                                                <Line
                                                    type="monotone"
                                                    dataKey="value"
                                                    name="Uptime"
                                                    stroke="#10b981"
                                                    strokeWidth={2}
                                                    dot={false}
                                                    activeDot={{ r: 4, strokeWidth: 0, fill: '#fff' }}
                                                />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>

                            {/* ISSUE TYPES TABLE (2/3 Width) */}
                            {(() => {
                                // Build rows from issueTypes: critical first, then warning
                                const issueTypeRows: IssueTypeRow[] = Object.entries(issues?.issueTypes || {}).map(([name, entry]) => ({
                                    issueName: name,
                                    severity: entry.severity,
                                    totalAssets: entry.totalAssets,
                                    assets: entry.assets,
                                }));

                                const critTypesCount = issueTypeRows.filter(r => r.severity === 'critical').length;
                                const warnTypesCount = issueTypeRows.filter(r => r.severity === 'warning').length;
                                const totalTypesCount = issueTypeRows.length;

                                const filteredRows = issueFilterSeverity
                                    ? issueTypeRows.filter(r => r.severity === issueFilterSeverity)
                                    : issueTypeRows;

                                const sorted = [...filteredRows].sort((a, b) => {
                                    if (a.severity === 'critical' && b.severity !== 'critical') return -1;
                                    if (a.severity !== 'critical' && b.severity === 'critical') return 1;
                                    return b.totalAssets - a.totalAssets;
                                });
                                const totalPages = Math.ceil(sorted.length / ROWS_PER_PAGE);
                                const paginated = sorted.slice(tablePage * ROWS_PER_PAGE, (tablePage + 1) * ROWS_PER_PAGE);

                                return (
                                    <div ref={issuesTableRef} className="w-1/3 bg-gray-800 rounded-xl border border-gray-700 p-2 shadow-sm flex flex-col">
                                        {/* Header */}
                                        <div className="flex items-center justify-between mb-2 flex-none border-b border-gray-700 pb-2">
                                            <h3 className="text-[11px] font-bold text-gray-100 uppercase tracking-wide flex items-center gap-2">
                                                <div className="p-1 rounded bg-rose-500/10 text-rose-400">
                                                    <ShieldAlert className="w-3.5 h-3.5" />
                                                </div>
                                                Critical &amp; Warnings
                                            </h3>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => {
                                                        setIssueFilterSeverity(issueFilterSeverity === 'critical' ? null : 'critical');
                                                        setTablePage(0);
                                                    }}
                                                    className={`text-[9px] font-bold px-2 py-0.5 rounded border transition-all ${issueFilterSeverity === 'critical'
                                                        ? 'bg-rose-500 text-white border-rose-400'
                                                        : 'text-rose-400 bg-rose-500/10 border-rose-500/20 hover:bg-rose-500/20'
                                                        } `}
                                                >
                                                    {critTypesCount} Critical
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setIssueFilterSeverity(issueFilterSeverity === 'warning' ? null : 'warning');
                                                        setTablePage(0);
                                                    }}
                                                    className={`text-[9px] font-bold px-2 py-0.5 rounded border transition-all ${issueFilterSeverity === 'warning'
                                                        ? 'bg-amber-500 text-gray-900 border-amber-400'
                                                        : 'text-amber-400 bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20'
                                                        } `}
                                                >
                                                    {warnTypesCount} Warning
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setIssueFilterSeverity(null);
                                                        setTablePage(0);
                                                    }}
                                                    className={`text-[9px] font-bold px-2 py-0.5 rounded border transition-all ${issueFilterSeverity === null
                                                        ? 'bg-gray-600 text-white border-gray-500'
                                                        : 'text-gray-100 bg-gray-700 border-gray-600 hover:bg-gray-600'
                                                        } `}
                                                >
                                                    {totalTypesCount} Total
                                                </button>
                                            </div>
                                        </div>

                                        {/* Table */}
                                        <div className="flex-1 min-h-0 flex flex-col">
                                            {/* Table Head */}
                                            <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 px-2 pb-1.5 border-b border-gray-700">
                                                <span className="text-[9px] font-bold text-gray-100 uppercase tracking-wider">Issue</span>
                                                <span className="text-[9px] font-bold text-gray-100 uppercase tracking-wider text-center">Severity</span>
                                                <span className="text-[9px] font-bold text-gray-100 uppercase tracking-wider text-center">Assets</span>
                                            </div>

                                            {/* Table Body */}
                                            <div className="overflow-y-auto custom-scroll">
                                                {sorted.length === 0 ? (
                                                    <div className="h-full flex flex-col items-center justify-center text-gray-100 py-10">
                                                        <CheckCircle2 className="w-8 h-8 text-emerald-500/30 mb-2" />
                                                        <p className="text-[10px] font-medium uppercase tracking-wider">All Systems Optimal</p>
                                                    </div>
                                                ) : (
                                                    paginated.map((row, idx) => {
                                                        const isCrit = row.severity === 'critical';
                                                        const isSelected = selectedIssueType?.issueName === row.issueName;
                                                        return (
                                                            <div
                                                                key={`${row.issueName}-${idx}`}
                                                                onClick={() => { setSelectedIssueType(isSelected ? null : row); setIssuePanelPage(0); }}
                                                                className={`grid grid-cols-[2fr_1fr_1fr] gap-2 items-center px-2 py-2 border-b border-gray-700/50 cursor-pointer transition-all
                                                                    ${isSelected
                                                                        ? 'bg-gray-600/60'
                                                                        : 'hover:bg-gray-700/60'
                                                                    }
`}
                                                            >
                                                                {/* Issue Name */}
                                                                <div className="flex flex-col overflow-hidden">
                                                                    <span className="text-[10px] font-bold text-gray-100 truncate" title={row.issueName}>{row.issueName}</span>
                                                                </div>

                                                                {/* Severity Badge */}
                                                                <div className="flex justify-center">
                                                                    {isCrit ? (
                                                                        <span className="flex items-center gap-1 text-[8px] font-bold text-rose-400 bg-rose-500/10 border border-rose-500/20 px-1.5 py-0.5 rounded-full">
                                                                            <XCircle className="w-2.5 h-2.5" />
                                                                            Critical
                                                                        </span>
                                                                    ) : (
                                                                        <span className="flex items-center gap-1 text-[8px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded-full">
                                                                            <AlertTriangle className="w-2.5 h-2.5" />
                                                                            Warning
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {/* Total Assets */}
                                                                <div className="flex justify-center">
                                                                    <span
                                                                        className={`text-[10px] font-bold px-2 py-0.5 rounded ${isCrit
                                                                            ? 'text-rose-400 bg-rose-500/10 border border-rose-500/20'
                                                                            : 'text-amber-400 bg-amber-500/10 border border-amber-500/20'
                                                                            } `}
                                                                    >
                                                                        {row.totalAssets}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>

                                            {/* Pagination */}
                                            {totalPages > 1 && (
                                                <div className="flex items-center justify-between pt-2 border-t border-gray-700 mt-1 flex-none">
                                                    <span className="text-[9px] text-gray-100">
                                                        Page {tablePage + 1} of {totalPages}
                                                    </span>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={() => setTablePage(p => Math.max(0, p - 1))}
                                                            disabled={tablePage === 0}
                                                            className="p-1 rounded bg-gray-700 border border-gray-600 text-gray-100 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            <ChevronLeft className="w-3 h-3" />
                                                        </button>
                                                        {getVisiblePages(tablePage, totalPages).map((pi: number) => (
                                                            <button
                                                                key={pi}
                                                                onClick={() => setTablePage(pi)}
                                                                className={`w-5 h-5 text-[9px] font-bold rounded transition-colors ${pi === tablePage
                                                                    ? 'bg-indigo-500 text-white'
                                                                    : 'bg-gray-700 text-gray-100 hover:bg-gray-600 border border-gray-600'
                                                                    } `}
                                                            >
                                                                {pi + 1}
                                                            </button>
                                                        ))}
                                                        <button
                                                            onClick={() => setTablePage(p => Math.min(totalPages - 1, p + 1))}
                                                            disabled={tablePage >= totalPages - 1}
                                                            className="p-1 rounded bg-gray-700 border border-gray-600 text-gray-100 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            <ChevronRight className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* EVENTS TABLE (1/3 Width) */}
                            <div className="w-1/3 bg-gray-800 rounded-xl border border-gray-700 p-2 shadow-sm flex flex-col">
                                <div className="flex items-center justify-between mb-2 flex-none border-b border-gray-700 pb-2">
                                    <h3 className="text-[11px] font-bold text-gray-100 uppercase tracking-wide flex items-center gap-2">
                                        <div className="p-1 rounded bg-indigo-500/10 text-indigo-400"><Activity className="w-3.5 h-3.5" /></div>
                                        Recent Events
                                    </h3>
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            onClick={() => {
                                                setEventSeverity(eventSeverity === 'critical' ? null : 'critical');
                                                setEventTablePage(0);
                                            }}
                                            className={`text-[9px] font-bold px-2 py-0.5 rounded border transition-all ${eventSeverity === 'critical'
                                                ? 'bg-rose-500 text-white border-rose-400'
                                                : 'text-rose-400 bg-rose-500/10 border-rose-500/20 hover:bg-rose-500/20'
                                                } `}
                                        >
                                            {eventSummary?.by_severity.critical || 0} Critical
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEventSeverity(eventSeverity === 'warning' ? null : 'warning');
                                                setEventTablePage(0);
                                            }}
                                            className={`text-[9px] font-bold px-2 py-0.5 rounded border transition-all ${eventSeverity === 'warning'
                                                ? 'bg-amber-500 text-gray-900 border-amber-400'
                                                : 'text-amber-400 bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20'
                                                } `}
                                        >
                                            {eventSummary?.by_severity.warning || 0} Warning
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEventSeverity(eventSeverity === 'info' ? null : 'info');
                                                setEventTablePage(0);
                                            }}
                                            className={`text-[9px] font-bold px-2 py-0.5 rounded border transition-all ${eventSeverity === 'info'
                                                ? 'bg-indigo-500 text-white border-indigo-400'
                                                : 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20 hover:bg-indigo-500/20'
                                                } `}
                                        >
                                            {eventSummary?.by_severity.info || 0} Info
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEventSeverity(null);
                                                setEventTablePage(0);
                                            }}
                                            className={`text-[9px] font-bold px-2 py-0.5 rounded border transition-all ${eventSeverity === null
                                                ? 'bg-gray-600 text-white border-gray-500'
                                                : 'text-gray-100 bg-gray-700 border-gray-600 hover:bg-gray-600'
                                                } `}
                                        >
                                            {totalEvents || 0} Total
                                        </button>
                                        <SingleDateRangeSelector onDateChange={(date) => setEventDateFilter(date)} />
                                    </div>
                                </div>

                                <div className="flex-1 min-h-0 flex flex-col">
                                    {/* Table Head */}
                                    <div className="grid grid-cols-[1.5fr_1.2fr_0.8fr_0.8fr] gap-2 px-2 pb-1.5 border-b border-gray-700">
                                        <span className="text-[9px] font-bold text-gray-100 uppercase tracking-wider">Asset</span>
                                        <span className="text-[9px] font-bold text-gray-100 uppercase tracking-wider">Event Type</span>
                                        <span className="text-[9px] font-bold text-gray-100 uppercase tracking-wider text-center">Status</span>
                                        <span className="text-[9px] font-bold text-gray-100 uppercase tracking-wider text-center">Severity</span>
                                    </div>

                                    {/* Table Body */}
                                    <div className="overflow-y-auto custom-scroll relative">
                                        {eventsLoading && (
                                            <div className="absolute inset-0 bg-gray-800/40 backdrop-blur-[1px] z-10 flex items-center justify-center">
                                                <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        )}
                                        {dashboardEvents.length === 0 ? (
                                            <div className="h-full flex flex-col items-center justify-center text-gray-100 py-10">
                                                <Activity className="w-8 h-8 text-indigo-500/30 mb-2" />
                                                <p className="text-[10px] font-medium uppercase tracking-wider">No events</p>
                                            </div>
                                        ) : (
                                            <>
                                                {dashboardEvents.map((evt) => {
                                                    const isSelected = selectedEvent?.id === evt.id;
                                                    const eventTypeStr = typeof evt.event_type === 'string' ? evt.event_type : (typeof evt.event_type === 'object' ? JSON.stringify(evt.event_type) : String(evt.event_type || ''));
                                                    const isOffline = eventTypeStr === 'camera_offline';
                                                    const isOnline = eventTypeStr === 'camera_online';
                                                    const typeColor = isOffline ? 'text-rose-400'
                                                        : isOnline ? 'text-emerald-400'
                                                            : 'text-indigo-400';
                                                    const hostnameStr = typeof evt.asset_hostname === 'object' ? JSON.stringify(evt.asset_hostname) : (evt.asset_hostname || (typeof evt.agent_name === 'object' ? JSON.stringify(evt.agent_name) : (evt.agent_name || '')));
                                                    const ipStr = typeof evt.asset_ip === 'object' ? JSON.stringify(evt.asset_ip) : String(evt.asset_ip || '');
                                                    const statusStr = typeof evt.event_status === 'object' ? JSON.stringify(evt.event_status) : (evt.event_status ? String(evt.event_status) : null);
                                                    const severityStr = typeof evt.severity === 'object' ? JSON.stringify(evt.severity) : String(evt.severity || '');
                                                    return (
                                                        <div
                                                            key={evt.id}
                                                            onClick={() => setSelectedEvent(isSelected ? null : evt)}
                                                            className={`grid grid-cols-[1.5fr_1.2fr_0.8fr_0.8fr] gap-2 items-center px-2 py-2 border-b border-gray-700/50 cursor-pointer transition-all
                                                                ${isSelected ? 'bg-gray-600/60' : 'hover:bg-gray-700/60'}
`}
                                                        >
                                                            <div className="flex flex-col overflow-hidden">
                                                                <span className="text-[10px] font-bold text-gray-100 truncate" title={hostnameStr}>{hostnameStr}</span>
                                                                <span className="text-[8px] text-gray-100 font-mono truncate">{ipStr}</span>
                                                            </div>
                                                            <div>
                                                                <span className={`text-[9px] font-bold ${typeColor} `}>
                                                                    {eventTypeStr.replace(/_/g, ' ')}
                                                                </span>
                                                            </div>
                                                            <div className="flex justify-center flex-col items-center">
                                                                {statusStr ? (
                                                                    <span className={`text-[8px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${statusStr === 'closed' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-amber-400 bg-amber-500/10 border-amber-500/20'}`}>
                                                                        {statusStr}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-[8px] font-bold px-2 py-0.5 rounded border text-gray-100 bg-gray-700 border-gray-600 uppercase tracking-wider">-</span>
                                                                )}
                                                            </div>
                                                            <div className="flex justify-center">
                                                                <span className={`text-[8px] font-bold px-2 py-0.5 rounded-full border ${severityStr === 'critical' ? 'text-rose-400 bg-rose-500/10 border-rose-500/20'
                                                                    : severityStr === 'warning' ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                                                                        : 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20'
                                                                    } `}>
                                                                    {severityStr}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </div>

                                    {/* Pagination */}
                                    {totalEvents > EVENTS_PER_PAGE && (() => {
                                        const totalEvtPages = Math.ceil(totalEvents / EVENTS_PER_PAGE);
                                        return (
                                            <div className="flex items-center justify-between pt-2 border-t border-gray-700 mt-1 flex-none">
                                                <span className="text-[9px] text-gray-100">
                                                    Page {eventTablePage + 1} of {totalEvtPages}
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => setEventTablePage(p => Math.max(0, p - 1))}
                                                        disabled={eventTablePage === 0}
                                                        className="p-1 rounded bg-gray-700 border border-gray-600 text-gray-100 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        <ChevronLeft className="w-3 h-3" />
                                                    </button>
                                                    {getVisiblePages(eventTablePage, totalEvtPages).map((pi: number) => (
                                                        <button
                                                            key={pi}
                                                            onClick={() => setEventTablePage(pi)}
                                                            className={`w-5 h-5 text-[9px] font-bold rounded transition-colors ${pi === eventTablePage
                                                                ? 'bg-indigo-500 text-white'
                                                                : 'bg-gray-700 text-gray-100 hover:bg-gray-600 border border-gray-600'
                                                                } `}
                                                        >
                                                            {pi + 1}
                                                        </button>
                                                    ))}
                                                    <button
                                                        onClick={() => setEventTablePage(p => Math.min(totalEvtPages - 1, p + 1))}
                                                        disabled={eventTablePage >= totalEvtPages - 1}
                                                        className="p-1 rounded bg-gray-700 border border-gray-600 text-gray-100 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        <ChevronRight className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>

                        </div>
                    </>
                )}
            </div>

            {/* RIGHT SLIDE PANEL — Issue Type Assets */}
            <AnimatePresence>
                {selectedIssueType && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedIssueType(null)}
                            className="fixed inset-0 bg-black/40 z-40"
                        />
                        {/* Panel */}
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-[650px] bg-gray-800 border-l border-gray-700 z-50 flex flex-col shadow-2xl"
                        >
                            {/* Panel Header */}
                            <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-700 ${selectedIssueType.severity === 'critical' ? 'bg-rose-500/5' : 'bg-amber-500/5'} `}>
                                <div className="flex items-center gap-2">
                                    <div className={`p-1.5 rounded ${selectedIssueType.severity === 'critical' ? 'bg-rose-500/15 text-rose-400' : 'bg-amber-500/15 text-amber-400'} `}>
                                        {selectedIssueType.severity === 'critical'
                                            ? <ShieldAlert className="w-4 h-4" />
                                            : <AlertTriangle className="w-4 h-4" />}
                                    </div>
                                    <div>
                                        <p className="text-[12px] font-bold text-gray-100 leading-tight">{selectedIssueType.issueName}</p>
                                        <p className="text-[9px] text-gray-100">{selectedIssueType.totalAssets} affected asset{selectedIssueType.totalAssets !== 1 ? 's' : ''}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedIssueType(null)}
                                    className="p-1.5 rounded-lg text-gray-100 hover:text-gray-100 hover:bg-gray-700 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Assets Table */}
                            {(() => {
                                const assets = selectedIssueType.assets;
                                const totalPages = Math.ceil(assets.length / ISSUE_PANEL_ROWS);
                                const paginated = assets.slice(issuePanelPage * ISSUE_PANEL_ROWS, (issuePanelPage + 1) * ISSUE_PANEL_ROWS);

                                return (
                                    <div className="flex-1 min-h-0 flex flex-col">
                                        {/* Table header */}
                                        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-gray-700 bg-gray-900/50 text-[9px] font-bold text-gray-100 uppercase tracking-wider">
                                            <div className="col-span-1">#</div>
                                            <div className="col-span-2">Asset</div>
                                            <div className="col-span-2">IP Address</div>
                                            <div className="col-span-4">Description</div>
                                            <div className="col-span-3">Recommendation</div>
                                        </div>

                                        {/* Table rows */}
                                        <div className="overflow-y-auto custom-scroll">
                                            {paginated.map((asset, idx) => {
                                                const globalIdx = issuePanelPage * ISSUE_PANEL_ROWS + idx;
                                                const isCrit = selectedIssueType.severity === 'critical';
                                                return (
                                                    <div
                                                        key={`${asset.asset_id}-${idx}`}
                                                        className={`grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-gray-700/50 hover:bg-gray-700/40 transition-colors items-start`}
                                                    >
                                                        <div className="col-span-1 text-[9px] text-gray-100 font-mono pt-0.5">{globalIdx + 1}</div>
                                                        <div className="col-span-2">
                                                            <p className="text-[10px] text-gray-100 font-semibold truncate" title={asset.asset_name}>{asset.asset_name}</p>
                                                            <p className="text-[8px] text-gray-100 font-mono">{new Date(asset.updated).toLocaleDateString()}</p>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <p className="text-[9px] text-gray-100 font-mono truncate">{asset.ip_address}</p>
                                                        </div>
                                                        <div className="col-span-4 min-w-0 overflow-hidden">
                                                            <div className="flex gap-1.5 items-start">
                                                                <AlertCircle className={`w-3 h-3 mt-0.5 shrink-0 ${isCrit ? 'text-rose-400' : 'text-amber-400'} `} />
                                                                <p className="text-[9px] text-gray-100 leading-relaxed break-all">{asset.issue.description}</p>
                                                            </div>
                                                        </div>
                                                        <div className="col-span-3 min-w-0 overflow-hidden">
                                                            <div className="flex gap-1.5 items-start">
                                                                <Lightbulb className={`w-3 h-3 mt-0.5 shrink-0 ${isCrit ? 'text-rose-400' : 'text-amber-400'} `} />
                                                                <p className="text-[9px] text-gray-100 leading-relaxed break-all">{asset.issue.recommendation}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-700 bg-gray-900/50">
                                                <span className="text-[9px] text-gray-100">
                                                    {issuePanelPage * ISSUE_PANEL_ROWS + 1}–{Math.min((issuePanelPage + 1) * ISSUE_PANEL_ROWS, assets.length)} of {assets.length}
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        disabled={issuePanelPage === 0}
                                                        onClick={() => setIssuePanelPage(p => Math.max(0, p - 1))}
                                                        className="w-6 h-6 flex items-center justify-center rounded bg-gray-700 text-gray-100 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-600"
                                                    >
                                                        <ChevronLeft className="w-3 h-3" />
                                                    </button>
                                                    {getVisiblePages(issuePanelPage, totalPages).map((pi: number) => (
                                                        <button
                                                            key={pi}
                                                            onClick={() => setIssuePanelPage(pi)}
                                                            className={`w-6 h-6 text-[9px] font-bold rounded transition-colors ${pi === issuePanelPage
                                                                ? 'bg-indigo-500 text-white'
                                                                : 'bg-gray-700 text-gray-100 hover:bg-gray-600 border border-gray-600'
                                                                } `}
                                                        >
                                                            {pi + 1}
                                                        </button>
                                                    ))}
                                                    <button
                                                        disabled={issuePanelPage >= totalPages - 1}
                                                        onClick={() => setIssuePanelPage(p => Math.min(totalPages - 1, p + 1))}
                                                        className="w-6 h-6 flex items-center justify-center rounded bg-gray-700 text-gray-100 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-600"
                                                    >
                                                        <ChevronRight className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* ── Event Detail Slide Panel ── */}
            <AnimatePresence>
                {selectedEvent && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedEvent(null)}
                            className="fixed inset-0 bg-black/40 z-40"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-[620px] bg-gray-800 border-l border-gray-700 z-50 flex flex-col shadow-2xl"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-indigo-500/5">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 rounded bg-indigo-500/15 text-indigo-400">
                                        <Activity className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[12px] font-bold text-gray-100 leading-tight">
                                            {typeof selectedEvent.asset_hostname === 'object' ? JSON.stringify(selectedEvent.asset_hostname) : (selectedEvent.asset_hostname || (typeof selectedEvent.agent_name === 'object' ? JSON.stringify(selectedEvent.agent_name) : (selectedEvent.agent_name || '')))}
                                        </p>
                                        <p className="text-[9px] text-gray-100 font-mono">
                                            {typeof selectedEvent.asset_ip === 'object' ? JSON.stringify(selectedEvent.asset_ip) : String(selectedEvent.asset_ip || '')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${selectedEvent.event_type === 'camera_offline' ? 'text-rose-400 bg-rose-500/10 border-rose-500/20'
                                        : selectedEvent.event_type === 'camera_online' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                                            : 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20'
                                        } `}>
                                        {typeof selectedEvent.event_type === 'string' ? selectedEvent.event_type.replace(/_/g, ' ') : (typeof selectedEvent.event_type === 'object' ? JSON.stringify(selectedEvent.event_type) : String(selectedEvent.event_type || ''))}
                                    </span>
                                    <button
                                        onClick={() => setSelectedEvent(null)}
                                        className="p-1.5 rounded-lg text-gray-100 hover:text-gray-200 hover:bg-gray-700 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Event Details */}
                            <div className="flex-1 min-h-0 overflow-y-auto custom-scroll p-4 flex flex-col gap-3 pb-20">
                                {/* Event Type Badge */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    {selectedEvent.event_status && (
                                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${selectedEvent.event_status === 'closed' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-amber-400 bg-amber-500/10 border-amber-500/20'}`}>
                                            {typeof selectedEvent.event_status === 'object' ? JSON.stringify(selectedEvent.event_status) : String(selectedEvent.event_status)}
                                        </span>
                                    )}
                                    {selectedEvent.ticket_number && (
                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded border text-blue-400 bg-blue-500/10 border-blue-500/20 uppercase tracking-wider cursor-pointer" onClick={() => window.open(`${localStorage.getItem('ticketing_domain')}/tickets/${typeof selectedEvent.ticket_id === 'object' ? (selectedEvent.ticket_id as any)?.id || JSON.stringify(selectedEvent.ticket_id) : selectedEvent.ticket_id}`, '_blank')}>
                                            <SquareArrowOutUpRight className="w-3 h-3 flex-shrink-0" />
                                            #{typeof selectedEvent.ticket_number === 'object' ? (selectedEvent.ticket_number as any).ticket_number || (selectedEvent.ticket_number as any).ticket_id || JSON.stringify(selectedEvent.ticket_number) : String(selectedEvent.ticket_number)}
                                        </span>
                                    )}
                                    <span className="text-[9px] text-gray-100 font-mono ml-auto">
                                        {selectedEvent.created ? (typeof selectedEvent.created === 'string' ? new Date(`${selectedEvent.created}${!selectedEvent.created.endsWith('Z') && !selectedEvent.created.includes('+') ? 'Z' : ''}`).toLocaleString() : String(selectedEvent.created)) : ''}
                                    </span>
                                </div>

                                {/* Message */}
                                <div className="rounded-lg border border-gray-700 bg-gray-700/30 p-3">
                                    <p className="text-[9px] font-bold text-gray-100 uppercase tracking-wider mb-1">Message</p>
                                    <p className="text-[11px] text-gray-100 leading-relaxed">{typeof selectedEvent.message === 'object' ? JSON.stringify(selectedEvent.message) : String(selectedEvent.message || '')}</p>
                                </div>

                                {/* Description */}
                                <div className="rounded-lg border border-gray-700 bg-gray-700/30 p-3">
                                    <p className="text-[9px] font-bold text-gray-100 uppercase tracking-wider mb-1">Description</p>
                                    <p className="text-[10px] text-gray-100 leading-relaxed">{typeof selectedEvent.description === 'object' ? JSON.stringify(selectedEvent.description) : String(selectedEvent.description || '')}</p>
                                </div>

                                {/* Agent */}
                                <div className="rounded-lg border border-gray-700 bg-gray-700/30 p-3">
                                    <p className="text-[9px] font-bold text-gray-100 uppercase tracking-wider mb-1">Agent</p>
                                    <p className="text-[10px] text-gray-100 truncate" title={typeof selectedEvent.agent_name === 'object' ? JSON.stringify(selectedEvent.agent_name) : String(selectedEvent.agent_name || '')}>
                                        {typeof selectedEvent.agent_name === 'object' ? JSON.stringify(selectedEvent.agent_name) : String(selectedEvent.agent_name || '')}
                                    </p>
                                </div>

                                {/* Event Data */}
                                {selectedEvent.event_data && (
                                    <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4 shadow-inner">
                                        <p className="text-[10px] font-bold text-gray-100 uppercase tracking-widest mb-3 border-b border-gray-700/50 pb-2">Event Metrics & Details</p>
                                        {(() => {
                                            try {
                                                let parsedData: any = selectedEvent.event_data;
                                                if (typeof parsedData === 'string') {
                                                    try {
                                                        parsedData = JSON.parse(parsedData);
                                                        if (typeof parsedData === 'string') {
                                                            parsedData = JSON.parse(parsedData);
                                                        }
                                                    } catch (e) {
                                                        parsedData = selectedEvent.event_data;
                                                    }
                                                }

                                                let timelineEvents: any[] = [];
                                                if (Array.isArray(parsedData)) {
                                                    timelineEvents = parsedData;
                                                } else if (typeof parsedData === 'object' && parsedData !== null) {
                                                    if (Array.isArray(parsedData.data)) {
                                                        timelineEvents = parsedData.data;
                                                    } else {
                                                        timelineEvents = [parsedData];
                                                    }
                                                }

                                                if (timelineEvents.length === 0) {
                                                    return (
                                                        <pre className="text-[9px] text-gray-400 font-mono leading-relaxed whitespace-pre-wrap break-all bg-gray-800/50 p-3 rounded border border-gray-700/50">
                                                            {typeof selectedEvent.event_data === 'object' ? JSON.stringify(selectedEvent.event_data, null, 2) : String(selectedEvent.event_data)}
                                                        </pre>
                                                    );
                                                }

                                                return (
                                                    <div className="relative border-l border-gray-600 ml-2 pl-3 py-1 space-y-3">
                                                        {timelineEvents.map((evtLog: any, idx: number) => {
                                                            if (!evtLog || typeof evtLog !== 'object') {
                                                                return (
                                                                    <div key={idx} className="text-[9px] text-gray-300 font-mono">
                                                                        {String(evtLog)}
                                                                    </div>
                                                                );
                                                            }
                                                            const d = (evtLog.data && typeof evtLog.data === 'object') ? evtLog.data : evtLog;
                                                            const status = typeof evtLog.status === 'string' ? evtLog.status : (typeof evtLog.status === 'object' ? JSON.stringify(evtLog.status) : 'info');
                                                            const statusStr = String(status);
                                                            const isClosed = statusStr.toLowerCase() === 'closed';
                                                            const isOpen = statusStr.toLowerCase() === 'open';

                                                            const badgeColor = isClosed ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20'
                                                                : isOpen ? 'text-rose-400 bg-rose-500/10 border border-rose-500/20'
                                                                    : 'text-amber-400 bg-amber-500/10 border border-amber-500/20';

                                                            const dotColor = isClosed ? 'bg-emerald-500 ring-gray-800 shadow-[0_0_8px_rgba(16,185,129,0.8)]'
                                                                : isOpen ? 'bg-rose-500 ring-gray-800 shadow-[0_0_8px_rgba(244,63,94,0.8)] animate-pulse'
                                                                    : 'bg-amber-500 ring-gray-800 shadow-[0_0_8px_rgba(245,158,11,0.8)]';

                                                            let eventDescription = '';
                                                            const evtTypeStr = typeof selectedEvent.event_type === 'string' ? selectedEvent.event_type : String(selectedEvent.event_type || '');
                                                            if (isOpen) {
                                                                if (evtTypeStr.includes('offline')) eventDescription = 'System detected asset went offline. Incident opened.';
                                                                else eventDescription = 'Event triggered and incident opened for review.';
                                                            } else if (isClosed) {
                                                                if (evtTypeStr.includes('offline') || evtTypeStr.includes('online')) eventDescription = 'Asset came back online. Incident resolved and closed.';
                                                                else eventDescription = 'Incident was resolved and marked as closed.';
                                                            } else {
                                                                eventDescription = 'Status updated to ' + statusStr;
                                                            }

                                                            const formattedDate = (evtLog.created && typeof evtLog.created === 'string') ? new Date(`${evtLog.created}${!evtLog.created.endsWith('Z') && !evtLog.created.includes('+') ? 'Z' : ''}`).toLocaleString(undefined, {
                                                                month: 'short', day: 'numeric', year: 'numeric',
                                                                hour: '2-digit', minute: '2-digit', second: '2-digit'
                                                            }) : (evtLog.created ? (typeof evtLog.created === 'object' ? JSON.stringify(evtLog.created) : String(evtLog.created)) : 'Unknown Time');

                                                            return (
                                                                <div key={idx} className="relative group">
                                                                    {/* Timeline Dot with Glow */}
                                                                    <div className="absolute -left-[16px] top-2.5 flex items-center justify-center z-10">
                                                                        <div className={`w-1.5 h-1.5 rounded-full ring-4 ${dotColor}`} />
                                                                    </div>

                                                                    {/* Timeline Content Card */}
                                                                    <div className={`bg-gray-800/60 rounded-xl border p-2.5 transition-all duration-300 ${isClosed ? 'border-emerald-500/20 hover:border-emerald-500/40 hover:bg-emerald-950/20'
                                                                        : isOpen ? 'border-rose-500/20 hover:border-rose-500/40 hover:bg-rose-950/20'
                                                                            : 'border-gray-700/80 hover:border-gray-600/80 hover:bg-gray-700/40'
                                                                        }`}>
                                                                        {/* Card Header */}
                                                                        <div className="flex items-center justify-between mb-1.5 border-b border-gray-700/50 pb-1.5">
                                                                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest ${badgeColor}`}>
                                                                                {statusStr}
                                                                            </span>
                                                                            <span className="text-[9px] text-gray-100 font-mono tracking-tight bg-gray-900/50 px-1.5 py-0.5 rounded border border-gray-700/50 shadow-inner">
                                                                                {formattedDate}
                                                                            </span>
                                                                        </div>

                                                                        {/* Description Text */}
                                                                        <div className="flex items-start gap-1.5 mb-1 text-[10px]">
                                                                            <div className={`w-1 h-1 rounded-full mt-1.5 shrink-0 ${isClosed ? 'bg-emerald-500' : isOpen ? 'bg-rose-500' : 'bg-amber-500'}`} />
                                                                            <p className="text-gray-300 font-medium leading-relaxed">{eventDescription}</p>
                                                                        </div>

                                                                        {/* Data details block */}
                                                                        {d && typeof d === 'object' && Object.keys(d).length > 0 && (
                                                                            <div className="bg-gray-900/60 rounded-lg border border-gray-700/50 p-2 mt-2 grid grid-cols-2 gap-y-2 gap-x-3 shadow-inner">
                                                                                {d.offline_start && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Offline Detected</span>
                                                                                        <span className="text-[9px] text-gray-100 font-mono mt-0.5">{typeof d.offline_start === 'object' ? JSON.stringify(d.offline_start) : new Date(`${d.offline_start}`).toLocaleString()}</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.online_again && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Back Online</span>
                                                                                        <span className="text-[9px] text-emerald-400 font-mono mt-0.5">{typeof d.online_again === 'object' ? JSON.stringify(d.online_again) : new Date(`${d.online_again}`).toLocaleString()}</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.duration_minutes !== undefined && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Total Downtime</span>
                                                                                        <span className="text-[9px] text-indigo-400 font-bold mt-0.5">{typeof d.duration_minutes === 'object' ? JSON.stringify(d.duration_minutes) : String(d.duration_minutes)} Minutes</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.failure_reason && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Root Cause</span>
                                                                                        <span className="text-[9px] text-rose-400 font-mono mt-0.5 tabular-nums">{typeof d.failure_reason === 'string' ? d.failure_reason.replace(/_/g, ' ') : JSON.stringify(d.failure_reason)}</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.ip_address && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Source IP</span>
                                                                                        <span className="text-[9px] text-gray-100 font-mono mt-0.5">{typeof d.ip_address === 'object' ? JSON.stringify(d.ip_address) : String(d.ip_address)}</span>
                                                                                    </div>
                                                                                )}

                                                                                {Object.entries(d)
                                                                                    .filter(([k]) => !['offline_start', 'online_again', 'duration_minutes', 'failure_reason', 'ip_address', 'ticket_description_html', 'warning_issues', 'critical_issues', 'assets', 'status', 'created', 'data'].includes(k))
                                                                                    .map(([k, v]) => (
                                                                                        <div className="flex flex-col" key={k}>
                                                                                            <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">{k.replace(/_/g, ' ')}</span>
                                                                                            <span className="text-[9px] text-gray-100 font-mono mt-0.5 overflow-hidden text-ellipsis truncate" title={typeof v === 'object' ? JSON.stringify(v) : String(v)}>
                                                                                                {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (typeof v === 'object' ? JSON.stringify(v) : String(v))}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))
                                                                                }

                                                                                {/* System Issues: Critical */}
                                                                                {(d.critical_issues && Array.isArray(d.critical_issues) && d.critical_issues.length > 0) && (
                                                                                    <div className="col-span-2 mt-2 space-y-2 border-t border-gray-700/50 pt-3">
                                                                                        <span className="text-[10px] text-rose-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                                                            <AlertTriangle className="w-3.5 h-3.5" />
                                                                                            Critical Issues ({d.critical_issues.length})
                                                                                        </span>
                                                                                        <div className="flex flex-col gap-1.5">
                                                                                            {d.critical_issues.map((iss: any, i: number) => (
                                                                                                <div key={i} className="bg-rose-950/20 border border-rose-900/30 rounded p-2.5">
                                                                                                    <div className="flex items-start justify-between mb-1 gap-2">
                                                                                                        <span className="text-[10px] font-bold text-rose-300 leading-tight">{typeof iss.issue === 'object' ? JSON.stringify(iss.issue) : String(iss.issue || '')}</span>
                                                                                                        <span className="text-[8px] px-1.5 py-0.5 bg-rose-500/20 text-rose-300 rounded uppercase shrink-0">{typeof iss.category === 'object' ? JSON.stringify(iss.category) : String(iss.category || '')}</span>
                                                                                                    </div>
                                                                                                    <p className="text-[9px] text-gray-400 leading-relaxed">{typeof iss.description === 'object' ? JSON.stringify(iss.description) : String(iss.description || '')}</p>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {/* System Issues: Warning */}
                                                                                {(d.warning_issues && Array.isArray(d.warning_issues) && d.warning_issues.length > 0) && (
                                                                                    <div className="col-span-2 mt-2 space-y-2 border-t border-gray-700/50 pt-3">
                                                                                        <span className="text-[10px] text-amber-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                                                            <AlertCircle className="w-3.5 h-3.5" />
                                                                                            Warning Issues ({d.warning_issues.length})
                                                                                        </span>
                                                                                        <div className="flex flex-col gap-1.5">
                                                                                            {d.warning_issues.map((iss: any, i: number) => (
                                                                                                <div key={i} className="bg-amber-950/20 border border-amber-900/30 rounded p-2.5">
                                                                                                    <div className="flex items-start justify-between mb-1 gap-2">
                                                                                                        <span className="text-[10px] font-bold text-amber-300 leading-tight">{typeof iss.issue === 'object' ? JSON.stringify(iss.issue) : String(iss.issue || '')}</span>
                                                                                                        <span className="text-[8px] px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded uppercase shrink-0">{typeof iss.category === 'object' ? JSON.stringify(iss.category) : String(iss.category || '')}</span>
                                                                                                    </div>
                                                                                                    <p className="text-[9px] text-gray-400 leading-relaxed">{typeof iss.description === 'object' ? JSON.stringify(iss.description) : String(iss.description || '')}</p>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {/* Discovered / Imported Assets */}
                                                                                {(d.assets && Array.isArray(d.assets) && d.assets.length > 0) && (
                                                                                    <div className="col-span-2 mt-2 space-y-2 border-t border-gray-700/50 pt-3">
                                                                                        <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                                                            <Server className="w-3.5 h-3.5" />
                                                                                            Assets ({d.assets.length})
                                                                                        </span>
                                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                                                                            {d.assets.map((asset: any, i: number) => (
                                                                                                <div key={i} className="bg-emerald-950/20 border border-emerald-900/30 rounded p-2.5 flex items-center justify-between">
                                                                                                    <div className="flex flex-col gap-0.5">
                                                                                                        <span className="text-[10px] font-bold text-emerald-300 leading-tight truncate max-w-[140px]" title={typeof asset.name === 'object' ? JSON.stringify(asset.name) : String(asset.name || 'Unknown Asset')}>{typeof asset.name === 'object' ? JSON.stringify(asset.name) : String(asset.name || 'Unknown Asset')}</span>
                                                                                                        <span className="text-[9px] text-gray-400 font-mono">{typeof asset.ip_address === 'object' ? JSON.stringify(asset.ip_address) : String(asset.ip_address || 'No IP')}</span>
                                                                                                    </div>
                                                                                                    <span className="text-[8px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded uppercase shrink-0">{typeof asset.asset_type === 'object' ? JSON.stringify(asset.asset_type) : String(asset.asset_type || 'Unknown')}</span>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            } catch (e) {
                                                return (
                                                    <pre className="text-[9px] text-gray-400 font-mono leading-relaxed whitespace-pre-wrap break-all bg-gray-800/50 p-3 rounded border border-gray-700/50">
                                                        {typeof selectedEvent.event_data === 'object' ? JSON.stringify(selectedEvent.event_data, null, 2) : String(selectedEvent.event_data)}
                                                    </pre>
                                                );
                                            }
                                        })()}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* ── Resource Utilization Slide Panel ── */}
            <AnimatePresence>
                {resourcePanelType && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setResourcePanelType(null)}
                            className="fixed inset-0 bg-black/40 z-40"
                        />
                        {/* Panel */}
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-[700px] bg-gray-800 border-l border-gray-700 z-50 flex flex-col shadow-2xl"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                                <div className="flex items-center gap-2">
                                    <div className={`p-1.5 rounded-lg ${resourcePanelType === 'memory' ? 'bg-violet-500/15 text-violet-400' : 'bg-sky-500/15 text-sky-400'} `}>
                                        <Cpu className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[12px] font-bold text-gray-100 leading-tight">
                                            {resourcePanelType === 'memory' ? 'Memory Utilization' : 'Storage Utilization'}
                                        </p>
                                        <p className="text-[9px] text-gray-100">All assets</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setResourcePanelType(null)}
                                    className="p-1.5 rounded-lg text-gray-100 hover:text-gray-100 hover:bg-gray-700 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Table */}
                            <div className="flex-1 min-h-0 overflow-y-auto custom-scroll">
                                {(() => {
                                    const isMemory = resourcePanelType === 'memory';
                                    const allData = isMemory ? allMemoryData : allStorageData;
                                    const totalPages = Math.ceil(allData.length / RESOURCE_ROWS_PER_PAGE);
                                    const paginated = allData.slice(
                                        resourcePanelPage * RESOURCE_ROWS_PER_PAGE,
                                        (resourcePanelPage + 1) * RESOURCE_ROWS_PER_PAGE
                                    );

                                    return (
                                        <div className="flex flex-col h-full">
                                            {/* Table header */}
                                            <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-gray-700 bg-gray-900/50 text-[9px] font-bold text-gray-100 uppercase tracking-wider sticky top-0">
                                                <div className="col-span-1">#</div>
                                                <div className="col-span-3">Asset Name</div>
                                                <div className="col-span-2">IP Address</div>
                                                <div className="col-span-3">OS</div>
                                                <div className="col-span-3 text-right">{isMemory ? 'RAM %' : 'Storage %'}</div>
                                            </div>

                                            {/* Table rows */}
                                            <div className="overflow-y-auto custom-scroll">
                                                {paginated.map((item, idx) => {
                                                    const pct = isMemory
                                                        ? (item as typeof allMemoryData[0]).ram_percentage
                                                        : (item as typeof allStorageData[0]).storage_percentage;
                                                    const isCrit = isMemory ? (pct ?? 0) > 80 : (pct ?? 0) > 90;
                                                    const isWarn = isMemory ? (pct ?? 0) > 60 && !isCrit : (pct ?? 0) > 80 && !isCrit;
                                                    const color = pct === null ? 'text-gray-100' : isCrit ? 'text-rose-400' : isWarn ? 'text-amber-400' : isMemory ? 'text-violet-400' : 'text-sky-400';
                                                    const barColor = pct === null ? '#334155' : isCrit ? '#f43f5e' : isWarn ? '#f59e0b' : isMemory ? '#8b5cf6' : '#0ea5e9';
                                                    const globalIdx = resourcePanelPage * RESOURCE_ROWS_PER_PAGE + idx;

                                                    return (
                                                        <div
                                                            key={item.asset_id}
                                                            className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-gray-700/50 hover:bg-gray-700/40 transition-colors items-center"
                                                        >
                                                            <div className="col-span-1 text-[9px] text-gray-100 font-mono">{globalIdx + 1}</div>
                                                            <div className="col-span-3">
                                                                <p className="text-[10px] text-gray-100 font-semibold truncate" title={item.asset_name}>{item.asset_name}</p>
                                                            </div>
                                                            <div className="col-span-2">
                                                                <p className="text-[9px] text-gray-100 font-mono truncate">{item.ip_address}</p>
                                                            </div>
                                                            <div className="col-span-3">
                                                                <p className="text-[9px] text-gray-100 truncate" title={item.os_name || '—'}>{item.os_name ? (item.os_name.length > 50 ? item.os_name.substring(0, 50) + '..' : item.os_name) : '—'}</p>
                                                            </div>
                                                            <div className="col-span-3">
                                                                <div className="flex items-center gap-2 justify-end">
                                                                    <div className="w-16 h-1.5 bg-gray-900 rounded-full overflow-hidden border border-gray-700">
                                                                        <div
                                                                            className="h-full rounded-full transition-all duration-500"
                                                                            style={{ width: `${pct ?? 0}%`, backgroundColor: barColor }}
                                                                        />
                                                                    </div>
                                                                    <span className={`text-[10px] font-mono font-bold ${color} min-w-[32px] text-right`}>
                                                                        {pct !== null ? `${pct}%` : "-"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Pagination */}
                                            {totalPages > 1 && (
                                                <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-700 bg-gray-900/50">
                                                    <span className="text-[9px] text-gray-100">
                                                        {resourcePanelPage * RESOURCE_ROWS_PER_PAGE + 1}–{Math.min((resourcePanelPage + 1) * RESOURCE_ROWS_PER_PAGE, allData.length)} of {allData.length}
                                                    </span>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            disabled={resourcePanelPage === 0}
                                                            onClick={() => setResourcePanelPage(p => Math.max(0, p - 1))}
                                                            className="w-6 h-6 flex items-center justify-center rounded bg-gray-700 text-gray-100 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-600"
                                                        >
                                                            <ChevronLeft className="w-3 h-3" />
                                                        </button>
                                                        {getVisiblePages(resourcePanelPage, totalPages).map((pi: number) => (
                                                            <button
                                                                key={pi}
                                                                onClick={() => setResourcePanelPage(pi)}
                                                                className={`w-6 h-6 text-[9px] font-bold rounded transition-colors ${pi === resourcePanelPage
                                                                    ? 'bg-indigo-500 text-white'
                                                                    : 'bg-gray-700 text-gray-100 hover:bg-gray-600 border border-gray-600'
                                                                    } `}
                                                            >
                                                                {pi + 1}
                                                            </button>
                                                        ))}
                                                        <button
                                                            disabled={resourcePanelPage >= totalPages - 1}
                                                            onClick={() => setResourcePanelPage(p => Math.min(totalPages - 1, p + 1))}
                                                            className="w-6 h-6 flex items-center justify-center rounded bg-gray-700 text-gray-100 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-600"
                                                        >
                                                            <ChevronRight className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Dashboard;
